<script setup lang="ts">
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { type MrBillItemsGroup, type MrBillItem } from './types';
import MrFlashMessage from '@components/base/mr-flash-message/MrFlashMessage.vue';

interface MrBillViewItem extends MrBillItem {
    divider?: boolean;
}
const {
    title = 'صورت‌حساب سفر',
    items = [],
    discounts = [],
    initialState = 'opened',
    hideCredit,
    loading,
} = defineProps<IProps>();
const autoReserveStore = useAutoReserveStore();
const authStore = useAuthStore();
const billElements = ref<HTMLElement[]>();
const discountElements = ref<HTMLElement[]>();
const containerElement = ref<HTMLElement>();

interface IProps {
    title?: string;
    items?: MrBillItemsGroup[];
    discounts?: MrBillItem[];
    initialState?: 'opened' | 'closed';
    hideCredit?: boolean;
    loading?: boolean;
    messages?: MessageItem[];
}
const expanded = ref(false);
const maxHeight = ref(0);
const currentCredit = computed(() => authStore.credit || 0);
const onlinePaymentText = computed(() => {
    if (autoReserveStore.isAutoReserve) {
        return 'شارژ آنلاین مورد نیاز';
    } else {
        return 'مبلغ قابل پرداخت';
    }
});
const showCredit = computed(
    () => authStore.credit !== null && !hideCredit && !loading
);
const unknownTotalPrice = computed(() =>
    items.some(({ items: groupedItems }) =>
        groupedItems.some(({ unknownPrice }) => unknownPrice)
    )
);
const remaining = computed(() =>
    Math.max(total.value - currentCredit.value, 0)
);
const normalizedItems = computed<MrBillViewItem[]>(() => {
    if (loading) {
        return [];
    }
    if (items.length > 1) {
        return items.reduce(
            (all, curr) => [
                ...all,
                {
                    title: curr.title ?? '',
                    count: 1,
                    unitPrice: curr.items.reduce(
                        (previousValue, currentValue) =>
                            previousValue +
                            currentValue.unitPrice * currentValue.count,
                        0
                    ),
                    divider: true,
                },
                ...curr.items,
            ],
            [] as MrBillViewItem[]
        );
    } else if (items.length) {
        return items[0].items;
    } else {
        return [];
    }
});
const normalizedDiscounts = computed<MrBillViewItem[]>(() => {
    if (loading) {
        return [];
    }
    const totalDiscount = discounts?.reduce(
        (previousValue, currentValue) =>
            previousValue + currentValue.unitPrice * currentValue.count,
        0
    );
    if (discounts?.length > 1 || items?.length > 1) {
        return [
            {
                title: 'تخفیف‌ها',
                unitPrice: totalDiscount,
                count: 1,
                divider: true,
            },
            ...discounts,
        ];
    } else {
        return discounts;
    }
});
const total = computed(() =>
    Math.max(
        normalizedItems.value.reduce(
            (prev, current) =>
                prev +
                (current.divider ? 0 : current.count) * current.unitPrice,
            0
        ) -
            discounts?.reduce(
                (prev, current) => prev + current.count * current.unitPrice,
                0
            ),
        0
    )
);
const billElementsHeight = computed(() => {
    const elementsHeight =
        billElements.value?.reduce((prev, el) => prev + el.clientHeight, 0) ||
        0;

    const discountsHeight =
        discountElements.value?.reduce(
            (prev, el) => prev + el.clientHeight,
            0
        ) || 0;
    return discountsHeight + elementsHeight;
});
const toggle = () => {
    expanded.value = !expanded.value;
};

watch(
    [
        billElementsHeight,
        () => containerElement.value?.clientHeight || 0,
        expanded,
        showCredit,
    ],
    () => {
        nextTick(() => {
            maxHeight.value = containerElement.value?.clientHeight || 0;
        });
    },
    {
        immediate: true,
    }
);
watch(
    () => initialState,
    val => {
        expanded.value = val === 'opened';
    },
    { immediate: true }
);
defineExpose({ toggle });
</script>
<template>
    <div class="mr-bill-root">
        <ClientOnly>
            <div class="desktop-header">
                <mr-typography weight="500">
                    {{ title }}
                </mr-typography>
                <mr-button
                    size="small"
                    variant="transparent"
                    :icon-button="expanded ? faChevronUp : faChevronDown"
                    @click.stop="toggle"
                />
            </div>
            <div class="mobile-header">
                <mr-button
                    size="medium"
                    variant="transparent"
                    :left-icon="!expanded ? faChevronUp : faChevronDown"
                    @click="expanded = !expanded"
                >
                    مجموع قیمت
                </mr-button>
                <div v-if="loading" class="loading-container">
                    <mr-fade-loading :scale="2" />
                </div>
                <mr-price-re
                    v-else
                    :price="total"
                    :unknown-price="unknownTotalPrice"
                    type="small"
                />
            </div>
            <div
                class="body"
                :style="{
                    '--max': expanded ? `${maxHeight}px` : 0,
                    '--mobile-max': expanded ? `${maxHeight}px` : 0,
                }"
            >
                <div ref="containerElement" class="padding-box">
                    <div
                        v-for="(item, index) in normalizedItems"
                        :key="`item-${index}`"
                        ref="billElements"
                        class="item"
                    >
                        <span
                            class="item-title"
                            :class="{ heading: item.divider }"
                        >
                            {{ item.divider ? '' : item.count }}
                            {{ item.title }}
                        </span>
                        <mr-divider v-if="item.divider" :thickness="1" />
                        <mr-price-re
                            :price="item.unitPrice"
                            :unknown-price="item.unknownPrice"
                            type="small"
                            :prefix="
                                item.count > 1 ? `x${item.count}` : undefined
                            "
                            free
                        />
                    </div>
                    <template v-if="discounts.length">
                        <div
                            v-for="(item, index) in normalizedDiscounts"
                            :key="`discount-${index}`"
                            ref="discountElements"
                            class="item"
                        >
                            <span
                                class="item-title"
                                :class="{ heading: item.divider }"
                            >
                                {{
                                    item.divider || item.count <= 1
                                        ? ''
                                        : item.count
                                }}
                                {{ item.title }}
                            </span>
                            <mr-divider v-if="item.divider" :thickness="1" />
                            <mr-price-re
                                :price="item.unitPrice"
                                type="small"
                                :prefix="
                                    item.count > 1
                                        ? `x${item.count}`
                                        : undefined
                                "
                                free
                            />
                        </div>
                    </template>
                    <mr-flash-message
                        v-show="messages?.length"
                        :items="messages"
                    />
                    <mr-divider class="desktop" :thickness="1" />
                    <div class="item desktop">
                        <span class="item-title">مجموع</span>
                        <div v-if="loading" class="loading-container">
                            <mr-fade-loading :scale="2" />
                        </div>
                        <mr-price-re
                            v-else
                            :price="total"
                            :unknown-price="unknownTotalPrice"
                        />
                    </div>
                    <template v-if="showCredit">
                        <mr-divider thickness="1px" />
                        <div class="item">
                            <span class="item-title">اعتبار موجود</span>
                            <mr-price-re :price="currentCredit" type="small" />
                        </div>
                        <div class="item">
                            <span class="item-title red">
                                {{ onlinePaymentText }}
                            </span>
                            <mr-price-re :price="remaining" type="small" />
                        </div>
                    </template>
                </div>
            </div>
        </ClientOnly>
    </div>
</template>

<style lang="scss" scoped>
.mr-bill-root {
    width: 100%;
    display: flex;
    flex-direction: column;
    .desktop-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        @include mobile-only {
            display: none;
        }
    }
    .mobile-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        @include desktop-only {
            display: none;
        }
        .loading-container {
            width: 32px;
        }
    }
    .body {
        transition: max-height 200ms ease;
        max-height: var(--max);
        @include mobile-only {
            max-height: var(--mobile-max);
        }
        overflow: hidden;
        .padding-box {
            display: flex;
            flex-direction: column;
            gap: 8px;
            padding-top: 8px;
            padding-bottom: 0;
            @include mobile-only {
                padding-bottom: 8px;
                padding-top: 0;
            }
            .item {
                display: flex;
                gap: 8px;
                align-items: center;
                .item-title {
                    @include tg-l;
                    white-space: wrap;
                    width: 100%;
                    flex-shrink: 1;
                    flex-grow: 1;
                    flex-basis: 100%;

                    &.heading {
                        @include tg-l($w5);
                    }
                    &.red {
                        color: usePalette($p-red, $p-500);
                    }
                }
                .loading-container {
                    width: 32px;
                }
            }
            .desktop {
                @include mobile-only {
                    display: none;
                }
            }
        }
    }
}
</style>
